/**
 * The browser's copy of everything the phone can be made to SAY.
 *
 * A hand-written mirror of the server's announcement types, and that is the
 * risk this file exists to make visible rather than to remove: nothing checks
 * the two against each other. A field the server starts sending arrives over
 * the wire, TypeScript is perfectly happy, and the control that should render
 * it simply never appears - no error, no warning, an empty space where a
 * setting used to be. Keeping the mirror in one small file at least means the
 * next person updating the server has one place to look.
 *
 * Split out of `telephony.ts`, which was over the size limit AGENTS.md §4 sets
 * and could not take another field. That file re-exports every name here, so no
 * import anywhere had to change.
 */
import { api } from "./api";

/** Everything the phone can be made to say. Mirrors `NOTICE_SLOTS`. */
export type NoticeSlot =
  | "notice.outbound.recorded"
  | "notice.outbound.processed"
  | "notice.inbound.recorded"
  | "notice.inbound.processed"
  | "inbound.voicemail"
  | "inbound.unavailable"
  | "hours.voicemail"
  | "hours.closed"
  // The caller menu's two questions. `menu.language` is the one slot with no
  // language tabs: it asks WHICH language, so it holds all three at once - see
  // `TRANSLATABLE_SLOTS` in the settings folder.
  | "menu.language"
  | "menu.department";

/** A recording generated from the settings page, as the server describes it. */
export interface NoticeClipInfo {
  id: string;
  /**
   * What spoke it: a TartuNLP speaker name or an ElevenLabs voice id.
   *
   * Stored verbatim and opaque, which is what lets an installation change
   * provider without a migration. The page resolves it into a readable name
   * against the voice list it has in hand and falls back to the stored value,
   * so a clip made by a voice the current provider does not offer still
   * describes itself honestly instead of guessing.
   */
  voice: string;
  bytes: number;
  /** Null when the synthesised audio could not be measured; never a guess. */
  durationMs: number | null;
  createdAt: string;
  /** The sentence the recording speaks, for comparing against the live field. */
  text: string;
  /** The saved wording has been edited since - the server's own comparison. */
  stale: boolean;
}

/** One announcement: a recorded clip, or a line for Twilio to read out. */
export interface NoticeVariant {
  audioUrl: string;
  text: string;
}

/** One direction's announcement: whether it plays, and the two wordings. */
export interface NoticeDirectionSettings {
  /** Play the announcement on calls in this direction. */
  enabled: boolean;
  /** Played when the call is recorded (and therefore also transcribed). */
  recorded: NoticeVariant;
  /** Played when nothing is recorded but the co-pilot is listening. */
  processed: NoticeVariant;
}

/**
 * The languages an announcement can be recorded in. Mirrors `NOTICE_LOCALES`.
 *
 * The same three the public privacy notice is published in, and that is not a
 * coincidence to be tidied: the announcement ends by naming that page, so a
 * language on one side and not the other would hand a caller half a
 * disclosure.
 */
export type NoticeLocale = "et" | "ru" | "en";

export const NOTICE_LOCALES: NoticeLocale[] = ["et", "ru", "en"];

/**
 * One language: whether it is in use, and what it says.
 *
 * The switch is a separate question from "is anything written". Without it,
 * taking a language out of use would mean deleting the sentence somebody wrote
 * and the recording somebody made, and typing both again to bring it back.
 */
export interface NoticeLocaleOverlay {
  /** Announce in this language when a call asks for it. */
  enabled: boolean;
  /** What it says, per announcement. */
  slots: Partial<Record<NoticeSlot, NoticeVariant>>;
}

export interface TelephonyNoticeSettings {
  /** Master switch. Off means no announcement in either direction. */
  enabled: boolean;
  language: string;
  voice: string;
  /** Calls WE place. */
  outbound: NoticeDirectionSettings;
  /** Calls that come IN to the Twilio number. */
  inbound: NoticeDirectionSettings;
  /**
   * The same announcements in other languages, beside the base rather than
   * inside it.
   *
   * OPTIONAL in this mirror although the server always sends it, and that is
   * deliberate: the settings page is also rendered against a status this file
   * cannot prove came from a build that has the field. Reading `?? {}` at the
   * two places that need it costs nothing; a required field would compile
   * cleanly and render a blank tab strip against an older server.
   */
  locales?: Partial<Record<NoticeLocale, NoticeLocaleOverlay>>;
}

/** One direction's fragment: the switch and either wording, all optional. */
export type NoticeDirectionPatch = Partial<{
  enabled: boolean;
  recorded: Partial<NoticeVariant>;
  processed: Partial<NoticeVariant>;
}>;

/** Who speaks an announcement clip. Mirrors the API's `TtsProvider`. */
export type TtsProvider = "tartunlp" | "elevenlabs";

/** One voice a synthesis service offers. */
export interface TtsVoice {
  /**
   * Sent back verbatim when generating, as a provider-prefixed reference
   * (`tartunlp:mari`, `elevenlabs:EXAVITQu4vr4xnSDxMaL`) - opaque to this side,
   * which is why there is no branch on the provider anywhere in the picker.
   */
  id: string;
  /** What to show in the menu. Already readable; the API owns the capitalising. */
  label: string;
  /**
   * Language codes as the provider states them, and the two do not agree:
   * TartuNLP answers ISO 639-3 ("est", "vro") and ElevenLabs ISO 639-1 ("et").
   * Used to sort and annotate, never to filter - a voice hidden because a
   * metadata field was blank is a voice removed by a sort function.
   */
  languages: string[];
  /** Who would synthesise it. The picker groups its rows on this. */
  provider: TtsProvider;
}

/** How one service's half of the list turned out. */
export interface TtsVoiceGroup {
  provider: TtsProvider;
  count: number;
  /** The list is the service's own rather than a fallback or an empty answer. */
  live: boolean;
  truncated: boolean;
  /** Why this service contributed nothing: "no_key", or its own failure reason. */
  error?: string;
}

export interface TtsVoiceListing {
  /**
   * The account's chosen synthesis service - the one `defaultVoice` belongs to.
   *
   * NOT "the only service in this list": `voices` holds everything reachable,
   * and a clip may be recorded in any of them.
   */
  provider: TtsProvider;
  /** Every voice reachable right now, the chosen provider's first. */
  voices: TtsVoice[];
  /** One entry per service that was asked, so the page can say what is missing. */
  groups: TtsVoiceGroup[];
  /**
   * False when the CHOSEN service did not answer and its half is a fallback or
   * empty. Worth saying out loud: generating may fail for the same reason the
   * listing did.
   */
  live: boolean;
  /**
   * What the picker opens on when a clip has no voice of its own. Empty when
   * there is nothing to generate with - an ElevenLabs provider with no key.
   */
  defaultVoice: string;
  /** More voices in the workspace than one page holds. ElevenLabs only. */
  truncated: boolean;
  /** Why the chosen service's list is empty when it is. */
  error?: string;
}

/** The recording a `generateNoticeClip` call produced. */
export interface GeneratedClip {
  id: string;
  /** Root-relative address to put in the announcement's audio field. */
  audioUrl: string;
  bytes: number;
  durationMs: number | null;
}

export function listTtsVoices(signal?: AbortSignal): Promise<TtsVoiceListing> {
  return api.get("/admin/crm/calls/tts/voices", signal);
}

export function generateNoticeClip(input: {
  slot: NoticeSlot;
  /**
   * Which language tab the recording is for. Omitted is the base wording.
   *
   * The server refuses a non-Estonian locale while the account synthesises with
   * TartuNLP, whose every speaker is Estonian - so the answer here decides
   * whether the button works, not just how the row is labelled.
   */
  locale?: NoticeLocale;
  /** Empty means the voice the shipped recordings were made in. */
  voice: string;
  text: string;
}): Promise<GeneratedClip> {
  return api.post("/admin/crm/calls/tts/clips", input);
}
